import React from 'react';

import { Button, Input, List } from 'antd';
import { Link, useRouteMatch } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';

import OperondaLogo from 'components/OperondaLogo';

import styles from './styles.module.less';
import { MENU_PATHS } from '../../constants';
import { useTimeTracker } from '../../contexts/TimeTrackerProvider';
import { ReactComponent as TimeTrackerLogo } from '@assets/images/time_tracker_logo.svg';
import { useAuthTimeTracker } from '../../contexts/AuthProvider';
import IntlMessages from '@src/util/IntlMessages';
import Title from '@src/components/BoxContainer/components/Title';
import CircleButton from '@src/components/Buttons/CircleButton';

const SEARCH_TYPES = {
  LIST: 'list',
  RUT: 'rut',
};

const cleanRutForSearch = value =>
  String(value ?? '')
    .trim()
    .replace(/[.\-\s]/g, '');

const PortalTable = ({ data = [], loading = false, onNext, onSearch }) => {
  const [searchType, setSearchType] = React.useState(null);
  let { url } = useRouteMatch();
  const { onSelectCorporation } = useTimeTracker();
  const { user } = useAuthTimeTracker();

  // We already have a formatRut function in the packages/utils/hooks/useRulesByCountry/constants.ts file
  // but we need to add a function to clean the rut for search according to DT requirements
  const formatRut = rut => {
    if (!rut) return '';
    const cleaned = String(rut).replace(/[^0-9kK]/g, '');
    if (cleaned.length < 2) return String(rut).trim();
    const body = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);
    if (!/^\d+$/.test(body) || !/^[0-9kK]$/.test(dv)) {
      return String(rut).trim();
    }
    const dvFormatted = /^[kK]$/.test(dv) ? 'K' : dv;
    return `${body}-${dvFormatted}`;
  };

  if (!searchType)
    return (
      <div className={styles.searchTypeContainer}>
        <div className="gx-flex-column gx-justify-content-center gx-align-items-center">
          <TimeTrackerLogo />
          <span>
            <Title.SubTitle value={<IntlMessages id="app.welcome" />} />
          </span>
          <span>{user?.email}</span>
        </div>
        <div className="gx-flex-column gx-mb-3 gx-mt-3 gx-align-items-center gx-guarnic-gap-2">
          <Button
            block
            className={styles.searchTypeButton}
            type="primary"
            onClick={() => setSearchType(SEARCH_TYPES.RUT)}
          >
            <IntlMessages id="work.direction.searchBy.rut" />
          </Button>
          <Button
            block
            className={styles.searchTypeButton}
            type="primary"
            onClick={() => setSearchType(SEARCH_TYPES.LIST)}
          >
            <IntlMessages id="work.direction.searchBy.list" />
          </Button>
        </div>
      </div>
    );

  return (
    <div className={styles.portalContainer}>
      <div className="gx-pt-3 gx-pb-2">
        <div className="gx-flex-column gx-justify-content-center gx-align-items-center">
          <div className="gx-flex-row gx-w-100">
            <div className="gx-w-25 gx-flex-row gx-justify-content-start">
              <CircleButton onClick={() => setSearchType(null)} iconName="arrow_back" />
            </div>
            <div className="gx-w-50 gx-flex-row gx-justify-content-center">
              <OperondaLogo />
            </div>
          </div>
          <span className="gx-guarnic-headline-1 gx-mb-4 gx-mt-3">
            <IntlMessages id="work.direction.search.employer" />
          </span>
        </div>
      </div>
      <>
        {searchType === SEARCH_TYPES.RUT && (
          <div>
            <Input.Search
              onChange={e => {
                onSearch(cleanRutForSearch(e.target.value));
              }}
            />
          </div>
        )}
        <div id="scrollableDiv" className={styles.scrolleableContainer}>
          <InfiniteScroll dataLength={data.length} scrollableTarget="scrollableDiv" next={onNext}>
            <List
              loading={loading}
              dataSource={data}
              renderItem={item => (
                <List.Item key={item.id}>
                  <List.Item.Meta
                    title={
                      <Link to={`${url}/${item.id}/${MENU_PATHS.ATTENDANCE_REPORT}`}>
                        <span
                          onClick={() =>
                            onSelectCorporation({ name: item?.name, id: item?.id, RUT: item?.RUT })
                          }
                        >
                          {item.name}
                        </span>
                      </Link>
                    }
                  />
                  <div className="gx-guarnic-description gx-mr-2">{formatRut(item.RUT)}</div>
                </List.Item>
              )}
            />
          </InfiniteScroll>
        </div>
      </>
    </div>
  );
};

export default React.memo(PortalTable);
